import { useState, useRef } from "react";

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const textarea = formRef.current?.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      alert("Message cannot exceed 500 characters.");
      return;
    }
    setStatus("submitting");

    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (typeof value === "string") data.append(key, value);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d74rqvecsbv12mkuda3g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        formRef.current?.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="w-full bg-[#0D0D0D] py-24 px-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Left Panel */}
        <div className="relative">
          <div className="relative overflow-hidden h-64 md:h-96 mb-8">
            <img
              src="https://public.readdy.ai/ai/img_res/0117c83eb34b4215ed221ebed74d6472.jpg"
              alt="Jordan Reyes silhouette against burning cityscape"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <span className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-[0.3em] uppercase block mb-2">
                [ Make Contact ]
              </span>
            </div>
          </div>

          <h2 className="font-['Special_Elite'] text-white text-4xl md:text-5xl leading-tight mb-6">
            If You&apos;re Out There,<br />
            <span className="text-[#C8922A]">Signal Us.</span>
          </h2>

          <p className="font-['Rajdhani'] text-[#888] text-base leading-relaxed mb-8">
            Leave a message. Drop coordinates. Tell us your story. In this world, connection is survival. Whether you have intel on Aidan&apos;s location, survivor reports, or just need to be heard — we listen.
          </p>

          {/* Info Items */}
          <div className="space-y-4">
            {[
              {
                icon: "ri-radio-line",
                label: "Frequency",
                value: "146.520 MHz — National Calling Frequency",
              },
              {
                icon: "ri-map-pin-line",
                label: "Last Known Coordinates",
                value: "Sector 7, Old Hotel District",
              },
              {
                icon: "ri-time-line",
                label: "Response Time",
                value: "Within 48 hours (conditions permitting)",
              },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-4">
                <div className="w-9 h-9 flex items-center justify-center border border-[#8B1A1A]/50 bg-[#8B1A1A]/10 flex-shrink-0">
                  <i className={`${item.icon} text-[#C8922A] text-base`} />
                </div>
                <div>
                  <div className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-widest uppercase mb-0.5">
                    {item.label}
                  </div>
                  <div className="font-['Rajdhani'] text-[#AAAAAA] text-sm">{item.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Form */}
        <div>
          <div className="border border-[#2C2C2C] p-8">
            <div className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-[0.3em] uppercase mb-2">
              [ Transmit Message ]
            </div>
            <h3 className="font-['Special_Elite'] text-white text-2xl mb-8">
              Send Word Through the Static
            </h3>

            {status === "success" ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center border border-[#C8922A] mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-[#C8922A] text-3xl" />
                </div>
                <h4 className="font-['Special_Elite'] text-white text-2xl mb-2">
                  Signal Received
                </h4>
                <p className="font-['Rajdhani'] text-[#888] text-sm">
                  Your message has been transmitted. We&apos;ll respond when the static clears.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-6 font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-widest uppercase border border-[#C8922A]/40 hover:border-[#C8922A] px-6 py-2 transition-colors cursor-pointer"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                data-readdy-form
                id="contact-form-wraith"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-widest uppercase block mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your name"
                      className="w-full bg-[#111] border border-[#2C2C2C] focus:border-[#8B1A1A] text-white font-['Rajdhani'] text-sm px-4 py-3 outline-none transition-colors placeholder-[#444]"
                    />
                  </div>
                  <div>
                    <label className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-widest uppercase block mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full bg-[#111] border border-[#2C2C2C] focus:border-[#8B1A1A] text-white font-['Rajdhani'] text-sm px-4 py-3 outline-none transition-colors placeholder-[#444]"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-widest uppercase block mb-2">
                    Subject
                  </label>
                  <select
                    name="subject"
                    required
                    className="w-full bg-[#111] border border-[#2C2C2C] focus:border-[#8B1A1A] text-[#AAAAAA] font-['Rajdhani'] text-sm px-4 py-3 outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Select a subject</option>
                    <option value="intel">Intel Report</option>
                    <option value="survivor">Survivor Account</option>
                    <option value="location">Location Data</option>
                    <option value="story">Share Your Story</option>
                    <option value="other">Other Transmission</option>
                  </select>
                </div>

                <div>
                  <label className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-widest uppercase block mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Transmit your message here... (max 500 characters)"
                    className="w-full bg-[#111] border border-[#2C2C2C] focus:border-[#8B1A1A] text-white font-['Rajdhani'] text-sm px-4 py-3 outline-none transition-colors resize-none placeholder-[#444]"
                  />
                </div>

                {status === "error" && (
                  <div className="border border-[#8B1A1A]/60 bg-[#8B1A1A]/10 px-4 py-3">
                    <p className="font-['Rajdhani'] text-[#CC6666] text-sm">
                      Transmission failed. Check your signal and try again.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full bg-[#8B1A1A] hover:bg-[#A52020] disabled:opacity-50 text-white font-['Share_Tech_Mono'] text-xs tracking-[0.2em] uppercase py-4 transition-all duration-300 cursor-pointer flex items-center justify-center gap-3 whitespace-nowrap"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" />
                      Transmitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-base" />
                      Transmit Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
